import { faArrowLeft as ArrowLeftIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import PonduranceIcon from '@/components/PonduranceIcon';
import StatusChip, { type StatusTypes } from '@/components/StatusChip';
import { PERMISSIONS } from '@/constants/auth.constants';
import { PONDURANCE_NAME } from '@/constants/common';
import { TICKET_THREAT_RESOLUTIONS } from '@/pages/Ticket/Ticket.constants';
import { ticketAtom } from '@/pages/Ticket/Ticket.store';
import type {
  PriorityDTO,
  TicketResolution
} from '@/pages/Ticket/Ticket.types';
import { isLayoutMobileAtom } from '@/store/layout';
import { userIsInternalAtom } from '@/store/user.store';
import getDateTime from '@/utils/getDateTime.utils';

import useStyles from './TicketDetailsSubTopbar.styles';

dayjs.extend(utc);

const TicketDetailsSubTopbarBottom = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [
    {
      acknowledged_at,
      alert_source,
      contained_at,
      external_ticket_id,
      detected_at,
      ingested_at,
      published_at,
      turbine_case_number,
      ticket_category,
      remediated_at,
      resolution_id,
      ticket_priority,
      ticket_status,
      ticket_type,
      updated_at,
      user
    }
  ] = useAtom(ticketAtom);
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const { mode: themeMode } = useColorScheme();
  const navigate = useNavigate();
  const priority = ticket_priority as PriorityDTO;
  const { classes } = useStyles({
    isLayoutMobile,
    priority,
    themeMode
  });

  const handleBack = () => {
    navigate({
      pathname: '/tickets'
    });
  };

  const resolution = useMemo(() => {
    return TICKET_THREAT_RESOLUTIONS.find(
      (r: TicketResolution) => r.id === resolution_id
    );
  }, [resolution_id]);

  return (
    <div
      className={classNames(
        classes.ticketDetailsSubTopbar,
        classes.ticketDetailsSubTopbarBottom
      )}
    >
      <div className={classes.subTopbarBottomContent}>
        {!isLayoutMobile && (
          <Button
            className={classes.bottomContentBackButton}
            color="secondary"
            onClick={handleBack}
            component={RouterLink}
            to="/tickets"
          >
            <FontAwesomeIcon icon={ArrowLeftIcon} size="lg" />
          </Button>
        )}
        <div className={classes.bottomContentSection}>
          <div className={classes.bottomContentGrid}>
            <div>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Status:
                </span>
              </Typography>
              <span className={classes.bottomContentSectionValues}>
                {ticket_status ? (
                  <StatusChip status={ticket_status.name as StatusTypes} />
                ) : (
                  '--'
                )}
              </span>
            </div>
            {resolution && resolution_id && (
              <div>
                <Typography variant="body2">
                  <span className={classes.bottomContentSectionKeys}>
                    Resolution:
                  </span>
                </Typography>
                <span className={classes.bottomContentSectionValues}>
                  <Chip
                    label={resolution?.text || ''}
                    color="secondary"
                    size="small"
                    variant="filled"
                  />
                </span>
              </div>
            )}
            <div>
              <Typography
                className={classes.bottomContentSectionKeys}
                variant="body2"
              >
                Category:
              </Typography>
              {ticket_category ? (
                <Chip
                  label={ticket_category.name}
                  color="secondary"
                  size="small"
                  variant="filled"
                />
              ) : (
                '--'
              )}
            </div>
            <div>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Author:
                </span>
              </Typography>
              <Stack
                direction="row"
                sx={{ alignItems: 'center' }}
                spacing={0.25}
              >
                {user?.internal && <PonduranceIcon />}
                <Typography
                  variant="body2"
                  className={classes.bottomContentSectionValues}
                >
                  {user?.internal && !userIsInternal
                    ? PONDURANCE_NAME
                    : user?.name}
                </Typography>
              </Stack>
            </div>
            <CheckPermissions permissions={[PERMISSIONS.INTERNAL]}>
              {external_ticket_id && (
                <div>
                  <Typography variant="body2">
                    <span className={classes.bottomContentSectionKeys}>
                      Jira ID:
                    </span>
                    <span className={classes.bottomContentSectionValues}>
                      <Link
                        color="primary"
                        href={`https://pondurance.atlassian.net/browse/${external_ticket_id}`}
                        rel="noreferrer"
                        target="_blank"
                        underline="none"
                        sx={{
                          textTransform: 'uppercase'
                        }}
                      >
                        {external_ticket_id}
                      </Link>
                    </span>
                  </Typography>
                </div>
              )}
            </CheckPermissions>
            <CheckPermissions permissions={[PERMISSIONS.CASES_READ]}>
              {turbine_case_number && (
                <div>
                  <Typography variant="body2">
                    <span className={classes.bottomContentSectionKeys}>
                      Case ID:
                    </span>
                    <span className={classes.bottomContentSectionValues}>
                      <RouterLink
                        color="primary"
                        to={
                          userIsInternal
                            ? `/case-internal/${turbine_case_number}`
                            : `/case/${turbine_case_number}`
                        }
                        style={{
                          textTransform: 'uppercase'
                        }}
                      >
                        {turbine_case_number}
                      </RouterLink>
                    </span>
                  </Typography>
                </div>
              )}
            </CheckPermissions>
            {alert_source && (
              <div>
                <Typography variant="body2">
                  <span className={classes.bottomContentSectionKeys}>
                    Alert Source:
                  </span>
                  <span className={classes.bottomContentSectionValues}>
                    {alert_source || '--'}
                  </span>
                </Typography>
              </div>
            )}
          </div>
        </div>
        {ticket_type === 1 && (
          <div className={classes.bottomContentSection}>
            <div className={classes.bottomContentGrid}>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Ingested At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {ingested_at ? getDateTime(ingested_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Detected At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {detected_at ? getDateTime(detected_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Created At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {published_at ? getDateTime(published_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Acknowledged At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {acknowledged_at ? getDateTime(acknowledged_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Remediated At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {remediated_at ? getDateTime(remediated_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Contained At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {contained_at ? getDateTime(contained_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Last Updated:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {updated_at ? getDateTime(updated_at) : '--'}
                </span>
              </Typography>
            </div>
          </div>
        )}
        {ticket_type === 2 && (
          <div className={classes.bottomContentSection}>
            <div className={classes.bottomContentGrid}>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Created At:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {published_at ? getDateTime(published_at) : '--'}
                </span>
              </Typography>
              <Typography variant="body2">
                <span className={classes.bottomContentSectionKeys}>
                  Last Updated:
                </span>
                <span className={classes.bottomContentSectionValues}>
                  {updated_at ? getDateTime(updated_at) : '--'}
                </span>
              </Typography>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetailsSubTopbarBottom;
